import React from 'react';
import { X } from 'lucide-react';
import type { TravelPlan } from '../../types/travel';

interface TravelPlanFormProps {
  plan?: TravelPlan;
  onSubmit: (data: {
    title: string;
    description?: string;
    startDate: Date;
    endDate: Date;
    location: string;
    purpose: 'business' | 'personal' | 'family' | 'vacation';
    budget?: number;
    notes?: string;
  }) => void;
  onClose: () => void;
}

export function TravelPlanForm({ plan, onSubmit, onClose }: TravelPlanFormProps) {

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    onSubmit({
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      startDate: new Date(formData.get('startDate') as string),
      endDate: new Date(formData.get('endDate') as string),
      location: formData.get('location') as string,
      purpose: formData.get('purpose') as 'business' | 'personal' | 'family' | 'vacation',
      budget: formData.get('budget') ? Number(formData.get('budget')) : undefined,
      notes: formData.get('notes') as string,
    });
  };

  return (
    <div className="modal-container">
      <div className="modal-content">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Plan New Trip</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Trip Name</label>
            <input
              type="text"
              name="title"
              defaultValue={plan?.title}
              placeholder="e.g., New York Business Trip"
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              name="description"
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white h-24 resize-none"
              defaultValue={plan?.description}
              placeholder="Brief description of your trip"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                defaultValue={plan?.startDate.toISOString().split('T')[0]}
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                name="endDate"
                defaultValue={plan?.endDate.toISOString().split('T')[0]}
                className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Destination</label>
            <input
              type="text"
              name="location"
              defaultValue={plan?.location}
              placeholder="City or Country"
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Purpose</label>
            <select
              name="purpose"
              defaultValue={plan?.purpose}
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white [&>option]:bg-[#2A2B2E]"
              required
            >
              <option value="business">Business</option>
              <option value="personal">Personal</option>
              <option value="family">Family</option>
              <option value="vacation">Vacation</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Budget</label>
            <input
              type="number"
              name="budget"
              defaultValue={plan?.budget}
              placeholder="Enter budget amount"
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Notes/Checklist</label>
            <textarea
              name="notes"
              className="w-full px-4 py-2 bg-black/20 rounded-xl text-white h-32 resize-none"
              defaultValue={plan?.notes}
              placeholder="Add packing list, reminders, or other notes"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#bbdde5] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
          >
            {plan ? 'Update Travel Plan' : 'Create Travel Plan'}
          </button>
        </form>
      </div>
    </div>
  );
}